import { ReactNode, useEffect, useMemo, useState } from "react";
import { Box, Button, Card, Flex, IconButton, Switch, Text, TextField } from "@radix-ui/themes";
import { ChevronDown, ChevronRight, Plus, Save, Trash2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import Modal from "@/components/ui/Modal";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { useEntities } from "@/hooks/useCommonQueries";
import { entityService } from "@/services/breeze/EntityService";
import { materialisedViewService } from "@/services/breeze/MaterialisedViewService";
import {
  ColumnDisplay,
  ColumnSourceKind,
  MaterialisedView,
  MaterialisedViewColumn,
  MaterialisedViewData,
} from "@/types/breeze/MaterialisedView";
import ChartFilterBuilder from "@/components/entity/charts/ChartFilterBuilder";
import { MaterialisedViewWidget } from "./widgets/MaterialisedViewWidget";
import { DashboardWidget } from "@/types/breeze/Dashboard";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (view: MaterialisedView) => void;
  existingViewId?: number | null;
}

const SOURCE_KINDS: { value: ColumnSourceKind; label: string }[] = [
  { value: "entity", label: "Any entity" },
  { value: "base_field", label: "Field on the row" },
  { value: "payments", label: "Payments" },
  { value: "subscriptions", label: "Subscriptions" },
  { value: "subscription_events", label: "Subscription events" },
  { value: "offer_redemptions", label: "Offer redemptions" },
];

const DISPLAY_TYPES: { value: ColumnDisplay; label: string }[] = [
  { value: "exists", label: "Marker (x)" },
  { value: "count", label: "1 if match, 0 otherwise" },
  { value: "value", label: "Actual field value" },
];

const TIMEFRAME_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "365d", label: "Last 365 days" },
];

function buildInitialDraft(): MaterialisedView {
  return {
    name: "",
    base_entity_name: null,
    columns: [],
  };
}

function newColumnId(): string {
  return `col_${Math.random().toString(36).slice(2, 9)}`;
}

export default function MaterialisedViewBuilderModal({ open, onOpenChange, onSaved, existingViewId }: Props) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<MaterialisedView>(() => buildInitialDraft());
  const [isSaving, setIsSaving] = useState(false);
  const isEditing = !!existingViewId;
  const { data: entities = [] } = useEntities(true);

  const { data: loadedView } = useQuery({
    queryKey: ["materialised-view", existingViewId],
    queryFn: () => materialisedViewService.getById(existingViewId!),
    enabled: open && !!existingViewId,
    staleTime: 0,
  });

  useEffect(() => {
    if (!open) {
      setDraft(buildInitialDraft());
      return;
    }
    if (loadedView) setDraft(loadedView);
  }, [open, loadedView]);

  const entityOptions = useMemo(() =>
    entities.map((e) => ({ value: e.name, label: e.name })),
    [entities],
  );

  const patch = (partial: Partial<MaterialisedView>) => setDraft((d) => ({ ...d, ...partial }));

  const addColumn = () => {
    const col: MaterialisedViewColumn = {
      id: newColumnId(),
      label: "",
      source: { kind: "entity", join_field: "user_id" },
      display: "exists",
      marker: "x",
    };
    patch({ columns: [...draft.columns, col] });
  };

  const updateColumn = (idx: number, partial: Partial<MaterialisedViewColumn>) => {
    patch({ columns: draft.columns.map((c, i) => (i === idx ? { ...c, ...partial } : c)) });
  };

  const removeColumn = (idx: number) => {
    patch({ columns: draft.columns.filter((_, i) => i !== idx) });
  };

  const resolvedColumns = useMemo(
    () => draft.columns.map((c) => ({ ...c, label: c.label?.trim() || autoColumnLabel(c) })),
    [draft.columns],
  );

  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageSize, setPreviewPageSize] = useState(50);

  const previewQuery = useQuery({
    queryKey: ["materialised-view-preview", draft, previewPage, previewPageSize],
    queryFn: () => materialisedViewService.previewConfig({
      name: draft.name || "Preview",
      description: draft.description ?? null,
      base_entity_name: draft.base_entity_name ?? null,
      columns: resolvedColumns,
      base_filters: draft.base_filters ?? null,
      timeframe_preset: draft.timeframe_preset ?? null,
      timeframe_from: draft.timeframe_from ?? null,
      timeframe_to: draft.timeframe_to ?? null,
      timeframe_offset_days: draft.timeframe_offset_days ?? null,
      compare_enabled: draft.compare_enabled ?? null,
      compare_previous_days: draft.compare_previous_days ?? null,
      row_limit: draft.row_limit ?? null,
      sort_column: draft.sort_column ?? null,
      sort_direction: draft.sort_direction ?? null,
      page: previewPage,
      page_size: previewPageSize,
    }),
    enabled: open && (draft.columns.length > 0 || !!draft.base_entity_name),
    staleTime: 5 * 1000,
  });

  const autoName = useMemo(() => {
    if (draft.columns.length === 0) return "";
    const base = draft.base_entity_name ? draft.base_entity_name.replace(/_/g, " ") : "Totals";
    const cols = draft.columns.length === 1
      ? draft.columns[0].label
      : `${draft.columns.length} columns`;
    return `${base} — ${cols}`;
  }, [draft.base_entity_name, draft.columns]);

  const handleSave = async () => {
    if (draft.columns.length === 0) { toast.error("Add at least one column"); return; }
    const finalName = draft.name.trim() || autoName;
    if (!finalName) { toast.error("Give the view a name"); return; }
    setIsSaving(true);
    try {
      const req = {
        name: finalName,
        description: draft.description ?? null,
        base_entity_name: draft.base_entity_name ?? null,
        columns: resolvedColumns,
        base_filters: draft.base_filters ?? null,
        timeframe_preset: draft.timeframe_preset ?? null,
        timeframe_from: draft.timeframe_from ?? null,
        timeframe_to: draft.timeframe_to ?? null,
        timeframe_offset_days: draft.timeframe_offset_days ?? null,
        compare_enabled: draft.compare_enabled ?? null,
        compare_previous_days: draft.compare_previous_days ?? null,
        row_limit: draft.row_limit ?? null,
        sort_column: draft.sort_column ?? null,
        sort_direction: draft.sort_direction ?? null,
      };
      const saved = isEditing
        ? await materialisedViewService.update(existingViewId!, req)
        : await materialisedViewService.create(req);
      await queryClient.invalidateQueries({ queryKey: ["materialised-views"] });
      toast.success(`View "${saved.name}" ${isEditing ? "updated" : "saved"}`);
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(`Failed to ${isEditing ? "update" : "save"} view`);
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const previewWidget: DashboardWidget = {
    id: 0,
    type: "materialised_view",
    title: draft.name || "Preview",
    configJson: "{}",
    x: 0, y: 0, w: 12, h: 6,
  } as unknown as DashboardWidget;

  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={isEditing ? "Edit materialised view" : "Design materialised view"}
      size="full"
      className="h-[90vh]"
    >
      <Flex direction="column" gap="4" p="2">
        <Flex gap="3" wrap="wrap" align="end">
          <Box style={{ flex: "2 1 300px", minWidth: 240 }}>
            <Text as="div" size="2" weight="medium" color="gray" mb="1">Name</Text>
            <TextField.Root
              value={draft.name}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder={autoName || "e.g. Recent user activity"}
            />
          </Box>
          <Box style={{ flex: "3 1 400px", minWidth: 240 }}>
            <Text as="div" size="2" weight="medium" color="gray" mb="1">Description (optional)</Text>
            <TextField.Root
              value={draft.description ?? ""}
              onChange={(e) => patch({ description: e.target.value || null })}
              placeholder="What does this view show?"
            />
          </Box>
          <Box style={{ flex: "1 1 220px", minWidth: 200 }}>
            <Text as="div" size="2" weight="medium" color="gray" mb="1">Show one row per</Text>
            <SearchableSelect
              value={draft.base_entity_name ?? ""}
              onChange={(v) => patch({ base_entity_name: v || null })}
              options={[{ value: "", label: "Totals only (no per-row detail)" }, ...entityOptions]}
              placeholder="Totals only"
            />
          </Box>
          <Button size="2" onClick={handleSave} disabled={isSaving}>
            <Save className="w-4 h-4" />
            {isSaving ? "Saving…" : (isEditing ? "Update view" : "Save view")}
          </Button>
        </Flex>

        {draft.base_entity_name && (
          <CollapsibleCard
            title="Base row filter"
            summary={draft.base_filters && draft.base_filters.length > 0
              ? `${draft.base_filters.length} filter${draft.base_filters.length === 1 ? "" : "s"} applied`
              : "No filters — all records included"}
            defaultOpen={!!(draft.base_filters && draft.base_filters.length > 0)}
          >
            <ChartFilterBuilder
              entityName={draft.base_entity_name}
              value={draft.base_filters ?? null}
              onChange={(filters) => patch({ base_filters: filters })}
            />
          </CollapsibleCard>
        )}

        <CollapsibleCard
          title="Timeframe & compare"
          summary={buildTimeframeSummary(draft)}
          defaultOpen={draft.timeframe_preset !== undefined && draft.timeframe_preset !== "all"
            || !!draft.timeframe_offset_days
            || !!draft.compare_enabled}
        >
          <Flex gap="3" wrap="wrap" align="end">
            <Box style={{ flex: "1 1 200px", minWidth: 160 }}>
              <Text as="div" size="1" color="gray" mb="1">Timeframe</Text>
              <SearchableSelect
                value={draft.timeframe_preset ?? "all"}
                onChange={(v) => patch({ timeframe_preset: v })}
                options={TIMEFRAME_OPTIONS}
              />
            </Box>
            <Box style={{ flex: "1 1 140px", minWidth: 120 }}>
              <Text as="div" size="1" color="gray" mb="1">Offset (days back)</Text>
              <TextField.Root
                type="number"
                min={0}
                step={1}
                placeholder="0"
                value={draft.timeframe_offset_days ?? ""}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  const n = v === "" ? null : Number(v);
                  patch({ timeframe_offset_days: n === null || Number.isNaN(n) || n < 0 ? null : Math.trunc(n) });
                }}
              />
            </Box>
            <Box style={{ flex: "0 0 auto" }}>
              <Text as="div" size="1" color="gray" mb="1">Compare vs prior</Text>
              <Flex align="center" gap="2" style={{ height: "32px" }}>
                <Switch
                  checked={draft.compare_enabled ?? false}
                  onCheckedChange={(v) => patch({ compare_enabled: v })}
                  size="2"
                />
                <Text size="1" color="gray">
                  {draft.compare_enabled ? "On" : "Off"}
                </Text>
              </Flex>
            </Box>
            {draft.compare_enabled && (
              <Box style={{ flex: "1 1 180px", minWidth: 160 }}>
                <Text as="div" size="1" color="gray" mb="1">Compare previous (days)</Text>
                <TextField.Root
                  type="number"
                  min={1}
                  step={1}
                  placeholder="Same as timeframe length"
                  value={draft.compare_previous_days ?? ""}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    const n = v === "" ? null : Number(v);
                    patch({ compare_previous_days: n === null || Number.isNaN(n) || n < 1 ? null : Math.trunc(n) });
                  }}
                />
              </Box>
            )}
          </Flex>
        </CollapsibleCard>

        <Card style={{ contain: "none" }}>
          <Box p="3">
            <Flex justify="between" align="center" mb="3">
              <Text as="div" size="3" weight="medium">Columns</Text>
              <Button size="1" variant="soft" onClick={addColumn}>
                <Plus className="w-4 h-4" /> Add column
              </Button>
            </Flex>
            {draft.columns.length === 0 && (
              <Text size="2" color="gray">No columns yet. Add one to start building the view.</Text>
            )}
            <Flex direction="column" gap="3">
              {draft.columns.map((col, idx) => (
                <ColumnRow
                  key={col.id}
                  column={col}
                  baseEntityName={draft.base_entity_name ?? null}
                  onChange={(partial) => updateColumn(idx, partial)}
                  onRemove={() => removeColumn(idx)}
                />
              ))}
            </Flex>
          </Box>
        </Card>

        {(draft.columns.length > 0 || !!draft.base_entity_name) && (
          <Card style={{ contain: "none" }}>
            <Box p="3" style={{ height: "400px" }}>
              <Text as="div" size="2" weight="medium" mb="2">Preview</Text>
              <MaterialisedViewWidget
                widget={previewWidget}
                data={previewQuery.data as MaterialisedViewData | undefined}
                loading={previewQuery.isFetching}
                page={previewPage}
                pageSize={previewPageSize}
                onPageChange={setPreviewPage}
                onPageSizeChange={(size) => { setPreviewPageSize(size); setPreviewPage(1); }}
              />
            </Box>
          </Card>
        )}
      </Flex>
    </Modal>
  );
}

interface ColumnRowProps {
  column: MaterialisedViewColumn;
  baseEntityName: string | null;
  onChange: (partial: Partial<MaterialisedViewColumn>) => void;
  onRemove: () => void;
}

function suggestJoinField(baseEntityName: string | null): string {
  if (!baseEntityName) return "user_id";
  // anythink_users → user_id, orders → order_id
  const stem = baseEntityName.replace(/^anythink_/, "").replace(/s$/, "");
  return `${stem}_id`;
}

const PAY_API_VALUE_FIELDS: Record<string, { value: string; label: string }[]> = {
  payments: [
    { value: "amount", label: "amount" },
    { value: "net_amount", label: "net_amount" },
    { value: "status", label: "status" },
    { value: "provider", label: "provider" },
    { value: "type", label: "type" },
    { value: "currency", label: "currency" },
    { value: "paid_at", label: "paid_at" },
  ],
  subscriptions: [
    { value: "amount", label: "amount" },
    { value: "status", label: "status" },
    { value: "provider", label: "provider" },
    { value: "type", label: "type" },
    { value: "billing_interval", label: "billing_interval" },
    { value: "currency", label: "currency" },
    { value: "trial_ends_at", label: "trial_ends_at" },
    { value: "cancelled_at", label: "cancelled_at" },
  ],
  subscription_events: [
    { value: "event_type", label: "event_type" },
    { value: "source", label: "source" },
    { value: "new_status", label: "new_status" },
    { value: "prior_status", label: "prior_status" },
    { value: "occurred_at", label: "occurred_at" },
  ],
  offer_redemptions: [
    { value: "offer_id", label: "offer_id" },
    { value: "redeemer_reward_status", label: "redeemer_reward_status" },
    { value: "redeemed_at", label: "redeemed_at" },
  ],
};

function autoColumnLabel(column: MaterialisedViewColumn): string {
  const src = column.source;
  const sourceName = src.kind === "entity"
    ? (src.entity ?? "entity").replace(/_/g, " ")
    : (SOURCE_KINDS.find((k) => k.value === src.kind)?.label.toLowerCase() ?? src.kind);
  switch (column.display) {
    case "exists":
      return `Any ${sourceName}`;
    case "count":
      return `${sourceName} count`;
    case "value":
      return column.value_field ? `${sourceName} ${column.value_field.replace(/_/g, " ")}` : sourceName;
    default:
      return sourceName;
  }
}

function ColumnRow({ column, baseEntityName, onChange, onRemove }: ColumnRowProps) {
  const { data: entities = [] } = useEntities(true);
  const entityOptions = useMemo(() =>
    entities.map((e) => ({ value: e.name, label: e.name })),
    [entities],
  );

  const isEntitySource = column.source.kind === "entity";
  const isValueDisplay = column.display === "value";
  const isExistsDisplay = column.display === "exists";
  const autoLabel = autoColumnLabel(column);

  const sourceEntityName = isEntitySource ? column.source.entity : null;
  const { data: sourceEntity } = useQuery({
    queryKey: ["entity", sourceEntityName, { includeSystem: true }],
    queryFn: () => entityService.getByName(sourceEntityName!, true),
    enabled: !!sourceEntityName,
    staleTime: 5 * 60 * 1000,
  });

  const valueFieldOptions = useMemo(() => {
    if (isEntitySource) {
      return sourceEntity?.fields
        ?.filter((f) => f.create_db_column)
        .map((f) => ({ value: f.name, label: f.name })) ?? [];
    }
    return PAY_API_VALUE_FIELDS[column.source.kind] ?? [];
  }, [isEntitySource, sourceEntity, column.source.kind]);

  return (
    <Card variant="surface" style={{ contain: "none" }}>
      <Box p="3">
        <Flex gap="3" wrap="wrap" align="end">
          <Box style={{ flex: "1 1 180px", minWidth: 140 }}>
            <Text as="div" size="1" color="gray" mb="1">Label (optional)</Text>
            <TextField.Root
              value={column.label.startsWith("Column ") ? "" : column.label}
              placeholder={autoLabel}
              onChange={(e) => onChange({ label: e.target.value || autoLabel })}
            />
          </Box>
          <Box style={{ flex: "1 1 200px", minWidth: 160 }}>
            <Text as="div" size="1" color="gray" mb="1">Source</Text>
            <SearchableSelect
              value={column.source.kind}
              onChange={(v) => onChange({ source: { ...column.source, kind: v as ColumnSourceKind } })}
              options={SOURCE_KINDS}
            />
          </Box>
          {isEntitySource && (
            <>
              <Box style={{ flex: "1 1 180px", minWidth: 160 }}>
                <Text as="div" size="1" color="gray" mb="1">Entity</Text>
                <SearchableSelect
                  value={column.source.entity ?? ""}
                  onChange={(v) => onChange({ source: { ...column.source, entity: v || null } })}
                  options={entityOptions}
                  placeholder="Pick entity"
                />
              </Box>
              <Box style={{ flex: "1 1 180px", minWidth: 160 }}>
                <Text as="div" size="1" color="gray" mb="1">Match on</Text>
                <SearchableSelect
                  value={column.source.join_field ?? suggestJoinField(baseEntityName)}
                  onChange={(v) => onChange({ source: { ...column.source, join_field: v || null } })}
                  options={valueFieldOptions}
                  placeholder={sourceEntityName ? `${suggestJoinField(baseEntityName)}` : "Pick entity first"}
                />
              </Box>
            </>
          )}
          <Box style={{ flex: "1 1 180px", minWidth: 160 }}>
            <Text as="div" size="1" color="gray" mb="1">Display</Text>
            <SearchableSelect
              value={column.display}
              onChange={(v) => onChange({ display: v as ColumnDisplay })}
              options={DISPLAY_TYPES}
            />
          </Box>
          {isValueDisplay && (
            <>
              <Box style={{ flex: "1 1 180px", minWidth: 140 }}>
                <Text as="div" size="1" color="gray" mb="1">Value field</Text>
                <SearchableSelect
                  value={column.value_field ?? ""}
                  onChange={(v) => onChange({ value_field: v || null })}
                  options={valueFieldOptions}
                  placeholder={isEntitySource && !sourceEntityName ? "Pick entity first" : "Pick field"}
                />
              </Box>
              <Box style={{ flex: "1 1 120px", minWidth: 100 }}>
                <Text as="div" size="1" color="gray" mb="1">Order</Text>
                <SearchableSelect
                  value={column.value_order ?? "latest"}
                  onChange={(v) => onChange({ value_order: v as "latest" | "first" })}
                  options={[{ value: "latest", label: "Latest" }, { value: "first", label: "First" }]}
                />
              </Box>
            </>
          )}
          {isExistsDisplay && (
            <Box style={{ flex: "1 1 100px", minWidth: 80 }}>
              <Text as="div" size="1" color="gray" mb="1">Marker</Text>
              <TextField.Root
                value={column.marker ?? "x"}
                onChange={(e) => onChange({ marker: e.target.value })}
              />
            </Box>
          )}
          <Box style={{ flex: "0 0 auto" }}>
            <Text as="div" size="1" color="gray" mb="1">&nbsp;</Text>
            <IconButton variant="ghost" color="red" onClick={onRemove}>
              <Trash2 className="w-4 h-4" />
            </IconButton>
          </Box>
        </Flex>
        {isEntitySource && column.source.entity && (
          <Box mt="3">
            <CollapsibleCard
              title="Column-level filter"
              summary={column.source.filters && column.source.filters.length > 0
                ? `${column.source.filters.length} filter${column.source.filters.length === 1 ? "" : "s"} applied`
                : "No filters — all records included"}
              defaultOpen={!!(column.source.filters && column.source.filters.length > 0)}
            >
              <ChartFilterBuilder
                entityName={column.source.entity}
                value={column.source.filters ?? null}
                onChange={(filters) => onChange({ source: { ...column.source, filters } })}
              />
            </CollapsibleCard>
          </Box>
        )}
      </Box>
    </Card>
  );
}

interface CollapsibleCardProps {
  title: string;
  summary?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

function CollapsibleCard({ title, summary, defaultOpen = false, children }: CollapsibleCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card style={{ contain: "none" }}>
      <Box p="3">
        <Flex align="center" gap="2" style={{ cursor: "pointer" }} onClick={() => setOpen((o) => !o)}>
          <IconButton variant="ghost" size="1" aria-label={open ? "Collapse" : "Expand"}>
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </IconButton>
          <Text as="div" size="2" weight="medium">{title}</Text>
          {summary && !open && <Text size="1" color="gray">— {summary}</Text>}
        </Flex>
        {open && <Box mt="3">{children}</Box>}
      </Box>
    </Card>
  );
}

function buildTimeframeSummary(draft: MaterialisedView): string {
  const preset = draft.timeframe_preset ?? "all";
  const presetLabel = TIMEFRAME_OPTIONS.find((o) => o.value === preset)?.label ?? preset;
  const offset = draft.timeframe_offset_days;
  const compare = draft.compare_enabled ? " · compare on" : "";
  const offsetStr = offset ? ` · offset ${offset}d` : "";
  return `${presetLabel}${offsetStr}${compare}`;
}
